/**
 * Scheduled events: something happening in a space at a set time, in a voice or stage channel or somewhere
 * else (a link, a place). Anyone in the space can say they are interested; people with Manage events (and bots
 * with that permission) create, change, start, end and cancel them.
 */
import { z } from 'zod';
import { idSchema } from './validation';

export const EVENT_LIMITS = {
  name: 100,
  description: 1000,
  location: 100,
  /** An event can be scheduled this far ahead. */
  maxAheadDays: 365,
  /** An event runs for at most this long. */
  maxLengthDays: 30,
  /** Events that are still to come or running, per space. Finished and canceled ones don't count. */
  maxLivePerSpace: 100,
  /** An event with no end time is closed this long after it starts, if nobody ends it. */
  openEndedHours: 12,
} as const;

/** scheduled -> active -> completed, or scheduled -> canceled. Nothing leaves completed or canceled. */
export type ScheduledEventStatus = 'scheduled' | 'active' | 'completed' | 'canceled';

export interface ScheduledEvent {
  id: string;
  spaceId: string;
  /** The voice or stage channel it takes place in, or null for an event somewhere else (see `location`). */
  channelId: string | null;
  creatorId: string | null;
  name: string;
  description: string;
  /** Where an event outside the app takes place (required then, and null for channel events). */
  location: string | null;
  startAt: string;
  endAt: string | null;
  status: ScheduledEventStatus;
  /** How many people said they are interested. */
  interestedCount: number;
  /** Whether the viewer said so. Left out when the viewer is not a person (events sent to everyone, or to bots). */
  interested?: boolean;
  createdAt: string;
}

const dateTime = z.string().datetime({ offset: true, message: 'Use a date and time like 2026-12-01T18:00:00Z' });

/** Date-time fields compared as instants, whatever offset they were written with. */
const ms = (value: string): number => new Date(value).getTime();

const nameSchema = z.string().trim().min(1, 'An event needs a name').max(EVENT_LIMITS.name);
const descriptionSchema = z.string().trim().max(EVENT_LIMITS.description);
const locationSchema = z.string().trim().min(1, 'Say where it takes place').max(EVENT_LIMITS.location);

/** The rules that depend only on what was sent. Whether the start is in the future is checked by the server. */
function checkTimes(value: { startAt?: string; endAt?: string | null }, ctx: z.RefinementCtx): void {
  if (value.startAt && value.endAt) {
    const start = ms(value.startAt);
    const end = ms(value.endAt);
    if (end <= start) ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'The event has to end after it starts' });
    else if (end - start > EVENT_LIMITS.maxLengthDays * 86_400_000) ctx.addIssue({ code: 'custom', path: ['endAt'], message: `An event can last at most ${EVENT_LIMITS.maxLengthDays} days` });
  }
}

/** What someone sends to schedule an event. */
export const scheduledEventCreateSchema = z
  .object({
    name: nameSchema,
    description: descriptionSchema.default(''),
    /** A voice or stage channel. Leave out (and give `location`) for an event somewhere else. */
    channelId: idSchema.nullish(),
    location: locationSchema.nullish(),
    startAt: dateTime,
    endAt: dateTime.nullish(),
  })
  .superRefine((e, ctx) => {
    if (e.channelId && e.location) ctx.addIssue({ code: 'custom', path: ['location'], message: 'An event in a channel has no separate location' });
    if (!e.channelId && !e.location) ctx.addIssue({ code: 'custom', path: ['location'], message: 'Pick a channel, or say where it takes place' });
    if (!e.channelId && !e.endAt) ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'An event outside the app needs an end time' });
    checkTimes(e, ctx);
  });

export type ScheduledEventInput = z.input<typeof scheduledEventCreateSchema>;
export type ScheduledEventData = z.output<typeof scheduledEventCreateSchema>;

/**
 * Changes to an event. Leave a field out to keep it. `status` moves an event along: `active` starts it now,
 * `completed` ends it now, `canceled` cancels one that has not started.
 */
export const scheduledEventUpdateSchema = z
  .object({
    name: nameSchema,
    description: descriptionSchema,
    channelId: idSchema.nullable(),
    location: locationSchema.nullable(),
    startAt: dateTime,
    endAt: dateTime.nullable(),
    status: z.enum(['active', 'completed', 'canceled']),
  })
  .partial()
  .superRefine((e, ctx) => {
    if (Object.keys(e).length === 0) ctx.addIssue({ code: 'custom', message: 'Nothing to change' });
    checkTimes(e, ctx);
  });

export type ScheduledEventUpdate = z.infer<typeof scheduledEventUpdateSchema>;

/** Sent to everyone in the space when an event is created or changed. Never says who is interested. */
export type ScheduledEventPayload = Omit<ScheduledEvent, 'interested'>;

/** Sent to everyone in the space when only the number of interested people changed. */
export interface ScheduledEventInterestUpdate {
  eventId: string;
  spaceId: string;
  interestedCount: number;
}

/** Bots hear who: someone said they are interested in an event, or took it back. */
export interface BotEventInterest {
  eventId: string;
  spaceId: string;
  /** The event's channel, or null for an event outside the app. */
  channelId: string | null;
  userId: string;
}

/** An event is gone: deleted, or its channel was deleted. */
export interface ScheduledEventDeleted {
  id: string;
  spaceId: string;
}
